import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  TablePagination,
} from "@mui/material";

import dayjs from "dayjs";

import * as actions from "../../redux/actions/index";
import Filter from "../../components/Filter/Filter.component";
import PageLoading from "../../components/PageLoading/PageLoading.component";
import { TablePaginationActions } from "../../components/TablePaginationActions/TablePaginationActions.component";
import { getSessionStorageItem } from "../../utils/getSessionStorageItem";
import { getStoredParams } from "../../utils/getStoredParams";
import { useLoadingAndResetRefs } from "../../utils/useLoadingAndResetRefs";
import { FIRST_PAGE, PAGE_SIZE_OPTIONS } from "../../utils/paginationConstants";

const Customers = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const customers = useSelector((state) => state.cmsCustomers.customers || []);
  const total = useSelector((state) => state.cmsCustomers.total || 0);
  const loading = useSelector(
    (state) => state.cmsCustomers.fetchCustomersLoading
  );
  const filtersLoading = useSelector(
    (state) => state.cmsCustomers.filtersLoading
  );
  const filtersApplied = useSelector(
    (state) => state.cmsCustomers.filtersApplied
  );
  const filtersReset = useSelector((state) => state.cmsCustomers.filtersReset);

  const [page, setPage] = useState(FIRST_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);
  const cmsAuth = useSelector((state) => state.cmsAuthenticator.cmsAuth || "");

  const storedParams = getStoredParams(location, getSessionStorageItem);

  const { isLoadingRef, isFiltersReset } = useLoadingAndResetRefs(
    loading,
    filtersReset
  );

  useEffect(() => {
    const fetchData = (params) => {
      dispatch(actions.onFetchCustomers(FIRST_PAGE, rowsPerPage, params));
    };

    if (filtersApplied && storedParams && !isLoadingRef.current) {
      fetchData(storedParams);
    } else if (!filtersApplied && !filtersLoading && !isFiltersReset.current) {
      fetchData(storedParams || null);
    }
  }, [
    dispatch,
    rowsPerPage,
    isLoadingRef,
    isFiltersReset,
    filtersApplied,
    filtersLoading,
    storedParams,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);

    const params = storedParams
      ? [newPage, rowsPerPage, storedParams]
      : [newPage, rowsPerPage];
    dispatch(actions.onFetchCustomers(...params));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(FIRST_PAGE);

    const params = storedParams
      ? [FIRST_PAGE, newRowsPerPage, storedParams]
      : [FIRST_PAGE, newRowsPerPage];
    dispatch(actions.onFetchCustomers(...params));
  };

  const restrict = cmsAuth.role !== "SUPER_ADMIN" && (
    <Navigate to="/dashboard" />
  );

  return (
    <>
      {restrict}
      <Filter page="customers" />
      <PageLoading loading={loading} />
      <div className="Customers">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left">ID</TableCell>
                <TableCell align="left">Name</TableCell>
                <TableCell align="left">FB ID</TableCell>
                <TableCell align="left">Mobile #</TableCell>
                <TableCell align="left">Total Points</TableCell>
                <TableCell align="left">Date Registered</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((customer, i) => {
                const customerFormatted = customer.created_at
                  ? dayjs(customer.created_at).format("MM/DD/YYYY h:mm A")
                  : null;
                const mobileNum = customer.mobile_number
                  ? `${customer.mobile_number.slice(0, 20)}...`
                  : "";
                return (
                  <TableRow key={i} hover>
                    <TableCell
                      component={Link}
                      to={`/customers/${customer.id}`}
                    >
                      {customer.id}
                    </TableCell>

                    <TableCell
                      component={Link}
                      to={`/customers/${customer.id}`}
                    >
                      {customer.name}
                    </TableCell>
                    <TableCell
                      component={Link}
                      to={`/customers/${customer.id}`}
                    >
                      {customer.fb_id}
                    </TableCell>
                    <TableCell
                      component={Link}
                      to={`/customers/${customer.id}`}
                    >
                      {mobileNum}
                    </TableCell>
                    <TableCell
                      align="center"
                      component={Link}
                      to={`/customers/${customer.id}`}
                    >
                      {customer.total_points}
                    </TableCell>
                    <TableCell
                      component={Link}
                      to={`/customers/${customer.id}`}
                    >
                      {customerFormatted}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                  colSpan={14}
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      "aria-label": "rows per page",
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default Customers;
